/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getSexObjectByName } from '@core/helpers/sex'
import {
  ALPHABETIC_REGEX,
  CONTAIN_DIGIT_REGEX,
  CONTAIN_SPECIAL_CHARACTER_REGEX,
  EMAIL_REGEX,
  LOWERCASE_REGEX,
  NUMERIC_REGEX,
  UPPERCASE_REGEX
} from '@core/lib/utils'
import useCatalogStore from '@core/stores/catalog/useCatalogStore'
import useRegisterStore from '@core/stores/register/useRegisterStore'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  initialForm,
  type RegisterFormData,
  registerSchema,
  type SuccessInputs
} from '@components/Register/RegisterForm/registerSchema'

export const useRegisterForm = () => {
  const { document, userInfo, lockInputs } = useRegisterStore()

  const { documentTypesCatalog: documentTypes, sexsCatalog: genders } = useCatalogStore()

  const [openTermConditonsModal, setOpenTermConditonsModal] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useForm<RegisterFormData>({
    mode: 'onSubmit',
    defaultValues: initialForm,
    resolver: zodResolver(registerSchema)
  })

  const [success, setSuccess] = useState({
    password: false,
    confirm_password: false,
    phone_number: false,
    email: false
  })

  const onChangeSuccess = (key: keyof SuccessInputs, value: boolean) => {
    setSuccess(prev => ({
      ...prev,
      [key]: value
    }))
  }
  const values = watch()

  useEffect(() => {
    if (!document.number || !document.type) return

    const values = getValues()

    reset({
      ...values,
      documentType: document.type,
      documentNumber: document.number
    })
  }, [document])

  useEffect(() => {
    if (!userInfo?.success) return

    const values = getValues()

    const { apellidoMaterno, apellidoPaterno, nombres, fechaNacimiento, sexo } = userInfo
    const sexId = getSexObjectByName(sexo).id
    reset({
      ...values,
      sex: sexId,
      born_date: fechaNacimiento,
      lastname1: apellidoPaterno,
      lastname2: apellidoMaterno,
      name: nombres
    })
  }, [userInfo])

  useEffect(() => {
    const subscription = watch(data => {
      const {
        phone_number: phoneNumber = '',
        password = '',
        confirm_password: confirmPassword = '',
        email = ''
      } = data

      const isValidPassword =
        LOWERCASE_REGEX.test(password) &&
        UPPERCASE_REGEX.test(password) &&
        CONTAIN_DIGIT_REGEX.test(password) &&
        CONTAIN_SPECIAL_CHARACTER_REGEX.test(password)

      onChangeSuccess('phone_number', phoneNumber?.length === 9)
      onChangeSuccess('email', EMAIL_REGEX.test(email))
      onChangeSuccess('password', isValidPassword)
      onChangeSuccess('confirm_password', isValidPassword && password === confirmPassword)
    })

    return () => subscription.unsubscribe()
  }, [])

  const onChangePassword = (type: 'password' | 'confirm_password', value: string) => {
    setValue(type, value, { shouldValidate: true })
  }

  const onChangePhoneNumber = (value: string) => {
    if (value.length > 0 && !NUMERIC_REGEX.test(value)) return
    if (value.length > 9) return
    setValue('phone_number', value, { shouldValidate: true })
  }
  const onChangeAlphabeticInput = (key: keyof RegisterFormData, value: string) => {
    if (value.length > 0 && !ALPHABETIC_REGEX.test(value)) return
    setValue(key, value, { shouldValidate: true })
  }

  return {
    values,
    success,
    errors,
    control,
    documentTypes,
    sexTypes: genders,
    lockInputs,
    openTermConditonsModal,

    onChangeAlphabeticInput,
    handleSubmit,
    onChangePhoneNumber,
    onChangePassword,
    setOpenTermConditonsModal
  }
}
